import React, { useState } from "react";
import {
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, CircularProgress,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const fmt = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));

// scripts: array of { ScriptID, InvAmount, WrittenOff, PaymentID }
// Pass invoiceID for invoice-level write-off, or scriptIDs ([ScriptID]) for single-script.
// Pass displayCount + displayTotal to show the button without per-script data (e.g. invoice totals).
export default function WriteOffButton({ claimID, invoiceID, scriptIDs, scripts = [], displayCount, displayTotal, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasOverride = displayCount != null;
  const eligible = hasOverride ? null : scripts.filter((s) => s.WrittenOff !== -1 && !s.PaymentID);

  if (!hasOverride && eligible.length === 0) return null;
  if (hasOverride && displayCount === 0) return null;

  const count = hasOverride ? displayCount : eligible.length;
  const total = hasOverride ? (displayTotal ?? 0) : eligible.reduce((sum, s) => sum + Number(s.InvAmount ?? 0), 0);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const body = { claimID };
      if (invoiceID != null) body.invoiceID = invoiceID;
      else body.scriptIDs = scriptIDs;
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_WRITE_OFF, "POST", body);
      setOpen(false);
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Write off">
        <IconButton size="small" onClick={() => setOpen(true)} sx={{ color: "#d32f2f", p: 0.25 }}>
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm write-off</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Write off {count} script{count !== 1 ? "s" : ""} totalling {fmt(total)}?{" "}
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Write off
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
